package org.dronda.app.web.repository

import com.github.michaelbull.result.Err
import com.github.michaelbull.result.Result
import com.github.michaelbull.result.getOrElse
import com.github.michaelbull.result.map
import com.github.michaelbull.result.mapError
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import org.dronda.app.web.client.AuthClient
import org.dronda.app.web.client.AuthResponse
import org.dronda.app.web.repository.util.decodeClaims
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

@OptIn(ExperimentalTime::class)
class AuthRepository(
    private val authClient: AuthClient
) {
    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    private val mutableLoginState = MutableStateFlow<State>(State.Loading)
    val loginState: StateFlow<State> = mutableLoginState.asStateFlow()

    suspend fun getValidAuthTokens(): AuthTokens? {
        val state = mutableLoginState.value
        if (state !is State.Loaded) {
            return null
        }

        if (state.tokens.expiration <= Clock.System.now()) {
            val tokens = tryRefreshTokens().getOrElse {
                mutableLoginState.compareAndSet(state, State.Empty)
                return null
            }
            return tokens
        }
        return state.tokens
    }

    fun getAuthTokens(): AuthTokens? {
        return (loginState.value as? State.Loaded)?.tokens
    }

    fun setAuthTokens(tokens: AuthResponse) {
        val authTokens = createAuthTokensObject(tokens.token, tokens.refreshToken) ?: return
        mutableLoginState.value = State.Loaded(authTokens)
    }

    suspend fun tryRefreshTokens(): Result<AuthTokens, Unit> {
        val state = mutableLoginState.value
        if (state !is State.Loaded) {
            return Err(Unit)
        }
        return tryRefreshTokens(state.tokens.token, state.tokens.refreshToken)
    }

    private suspend fun tryRefreshTokens(accessToken: String, refreshToken: String): Result<AuthTokens, Unit> {
        return authClient.refreshTokens(accessToken, refreshToken).map { tokens ->
            val tokens = createAuthTokensObject(tokens.token, tokens.refreshToken) ?: return Err(Unit)
            mutableLoginState.value = State.Loaded(tokens)
            tokens
        }.mapError {
            mutableLoginState.value = State.Empty
            Err(Unit)
        }
    }

    private fun createAuthTokensObject(accessToken: String, refreshToken: String): AuthTokens? {
        val expiration = decodeClaims(accessToken)?.exp ?: return null
        return AuthTokens(
            accessToken,
            refreshToken,
            Instant.fromEpochSeconds(expiration)
        )
    }

    init {
        scope.launch {
            val authTokens = ClientStorageRepository.getItem<AuthTokens>(TOKEN_STORAGE_KEY)
            if (authTokens != null) {
                if (authTokens.expiration <= Clock.System.now()) {
                    tryRefreshTokens(accessToken = authTokens.token, refreshToken = authTokens.refreshToken)
                } else {
                    mutableLoginState.value = State.Loaded(authTokens)
                }
            } else {
                mutableLoginState.value = State.Empty
            }
        }
        scope.launch {
            loginState.collect { state ->
                if (state is State.Loaded) {
                    val authTokens = createAuthTokensObject(state.tokens.token, state.tokens.refreshToken)
                    ClientStorageRepository.storeItem(TOKEN_STORAGE_KEY, authTokens)
                }
            }
        }
    }

    sealed interface State {
        object Loading : State
        object Empty : State
        data class Loaded(val tokens: AuthTokens) : State
    }

    companion object {
        private const val TOKEN_STORAGE_KEY = "token_storage_key"
    }
}


@Serializable
data class AuthTokens(
    val token: String,
    val refreshToken: String,
    val expiration: Instant
)
